import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiMail, FiLock, FiAlertCircle, FiCheckCircle, FiEye, FiEyeOff, FiMessageSquare, FiArrowLeft } from 'react-icons/fi';
import { GoogleLogin } from '@react-oauth/google';
import { motion } from 'framer-motion';

import loginHero from '../assests/Login/login.png';

// Change these if your routes are named differently
const TERMS_PATH = '/terms';
const PRIVACY_PATH = '/privacy';

const primaryBtn =
    'flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-red-600 text-base font-semibold text-white shadow-lg shadow-red-200 transition hover:bg-red-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none';

const IndiaFlag = () => (
    <svg viewBox="0 0 36 24" className="h-5 w-8 shrink-0 rounded-[3px] shadow-sm" aria-hidden="true">
        <rect width="36" height="8" fill="#FF9933" />
        <rect y="8" width="36" height="8" fill="#FFFFFF" />
        <rect y="16" width="36" height="8" fill="#138808" />
        <circle cx="18" cy="12" r="3" fill="none" stroke="#000080" strokeWidth="0.8" />
        <circle cx="18" cy="12" r="0.6" fill="#000080" />
    </svg>
);

const Spinner = () => (
    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
);

// Outlined field with the label sitting on the border (like the app screen)
function Field({ id, label, children }) {
    return (
        <div className="relative rounded-2xl border border-gray-300 bg-white transition focus-within:border-red-500 focus-within:ring-4 focus-within:ring-red-100">
            <label htmlFor={id} className="absolute -top-2.5 left-4 bg-white px-2 text-xs font-medium text-gray-500">
                {label}
            </label>
            {children}
        </div>
    );
}

export default function Login() {
    const [tab, setTab] = useState('phone'); // 'phone' | 'email' (phone-first)
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    // Phone OTP state
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();
    const { login, loginWithGoogle, sendPhoneOtp, verifyPhoneOtp } = useAuth();

    const from = location.state?.from?.pathname || '/account';

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const goAfterLogin = (msg) => {
        setSuccess(msg || 'Login successful! Redirecting...');
        setTimeout(() => navigate(from, { replace: true }), 1000);
    };

    const switchTab = (next) => {
        setTab(next);
        setError('');
        setSuccess('');
    };

    // ✅ EMAIL/PASSWORD LOGIN
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const result = await login(email, password);

        if (result.success) {
            goAfterLogin('Login successful! Redirecting...');
        } else {
            setError(result.message);
        }

        setLoading(false);
    };

    // ✅ GOOGLE LOGIN - Uses AuthContext
    const handleGoogleSignIn = async (response) => {
        setGoogleLoading(true);
        setError('');
        setSuccess('');

        const result = await loginWithGoogle(response.credential);

        if (result.success) {
            goAfterLogin('Google Sign-In successful! Redirecting...');
        } else {
            setError(result.message);
        }

        setGoogleLoading(false);
    };

    const handleGoogleError = () => {
        setError('Google sign-in was cancelled or could not be started. Please try again.');
    };

    // ✅ PHONE: SEND OTP
    const handleSendOtp = async (e) => {
        e?.preventDefault?.();
        const digits = phone.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '').replace(/^0(?=\d{10}$)/, '');
        if (!/^[6-9]\d{9}$/.test(digits)) {
            setError('Enter a valid 10-digit mobile number');
            return;
        }
        setOtpLoading(true);
        setError('');
        setSuccess('');
        const result = await sendPhoneOtp(digits);
        if (result.success) {
            setOtpSent(true);
            setSuccess(result.message || 'OTP sent to your mobile number');
            setCooldown(30);
        } else {
            setError(result.message);
        }
        setOtpLoading(false);
    };

    // ✅ PHONE: VERIFY OTP
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!/^\d{4,6}$/.test(otp.trim())) {
            setError('Enter the 4-digit OTP sent to your phone');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        const result = await verifyPhoneOtp(phone.replace(/\D/g, ''), otp.trim());
        if (result.success) {
            goAfterLogin('Phone verified! Redirecting...');
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white pt-16 lg:flex lg:items-center lg:justify-center lg:px-6 lg:py-10">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="mx-auto w-full max-w-md overflow-hidden bg-white sm:rounded-b-[2rem] lg:grid lg:max-w-5xl lg:grid-cols-2 lg:rounded-[2rem] lg:border lg:border-gray-100 lg:shadow-2xl"
            >
                {/* ---------- HERO ---------- */}
                <div className="relative overflow-hidden rounded-b-[2.5rem] bg-rose-50 lg:min-h-[640px] lg:rounded-none">
                    <img
                        src={loginHero}
                        alt="Havbit - all your trusted food brands in one place"
                        className="block object-cover h-auto w-full  object-top lg:absolute lg:inset-0 lg:h-full"
                    />
                </div>

                {/* ---------- FORM ---------- */}
                <div className="px-6 pb-10 pt-8 sm:px-8 lg:flex lg:flex-col lg:justify-center lg:px-14 lg:py-12">
                    <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-[2.6rem]">
                        Welcome <span className="text-red-600">back!</span>
                    </h1>
                    <p className="mt-2 text-base text-gray-500">Log in to continue your food journey with Havbit.</p>

                    <div className="mt-8">
                        {success && (
                            <div role="status" className="mb-5 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3.5 text-sm text-green-700">
                                <FiCheckCircle className="shrink-0" />
                                <span>{success}</span>
                            </div>
                        )}
                        {error && (
                            <div role="alert" className="mb-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700">
                                <FiAlertCircle className="mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {tab === 'phone' ? (
                            !otpSent ? (
                                <form onSubmit={handleSendOtp} className="space-y-4">
                                    <Field id="login-phone" label="Enter Phone Number">
                                        <div className="flex h-14 items-center gap-3 px-4">
                                            <IndiaFlag />
                                            <span className="text-base font-medium text-gray-800">+91</span>
                                            <span className="h-6 w-px bg-gray-200" aria-hidden="true" />
                                            <input
                                                id="login-phone"
                                                type="tel"
                                                inputMode="numeric"
                                                autoComplete="tel-national"
                                                required
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 13))}
                                                placeholder="00000 00000"
                                                maxLength="13"
                                                disabled={otpLoading || loading}
                                                className="min-w-0 flex-1 bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400"
                                            />
                                        </div>
                                    </Field>
                                    <button type="submit" disabled={otpLoading} className={primaryBtn}>
                                        {otpLoading ? (<><Spinner /> Sending OTP...</>) : 'Continue'}
                                    </button>
                                    <p className="text-center text-xs text-gray-400">A 4-digit OTP will be sent by SMS. New users are registered automatically.</p>
                                </form>
                            ) : (
                                <form onSubmit={handleVerifyOtp} className="space-y-4">
                                    <Field id="login-otp" label={`OTP sent to +91 ${phone.replace(/\D/g, '').slice(-10)}`}>
                                        <div className="flex h-14 items-center gap-3 px-4">
                                            <FiMessageSquare className="h-5 w-5 shrink-0 text-gray-400" />
                                            <input
                                                id="login-otp"
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                                autoFocus
                                                required
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                placeholder="••••"
                                                maxLength="6"
                                                disabled={loading}
                                                className="min-w-0 flex-1 bg-transparent text-center text-xl font-semibold tracking-[0.5em] text-gray-900 outline-none placeholder:text-gray-300"
                                            />
                                        </div>
                                    </Field>
                                    <button type="submit" disabled={loading} className={primaryBtn}>
                                        {loading ? (<><Spinner /> Verifying...</>) : 'Verify & Sign In'}
                                    </button>
                                    <div className="flex items-center justify-between text-sm">
                                        <button
                                            type="button"
                                            onClick={() => { setOtpSent(false); setOtp(''); setError(''); setSuccess(''); }}
                                            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800"
                                        >
                                            <FiArrowLeft /> Change number
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSendOtp}
                                            disabled={otpLoading || cooldown > 0}
                                            className="font-semibold text-red-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                                        >
                                            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                                        </button>
                                    </div>
                                    <p className="text-center text-xs text-gray-400">OTP is valid for 5 minutes.</p>
                                </form>
                            )
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <Field id="login-email" label="Email Address">
                                    <div className="flex h-14 items-center gap-3 px-4">
                                        <FiMail className="h-5 w-5 shrink-0 text-gray-400" />
                                        <input
                                            id="login-email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@example.com"
                                            disabled={loading || googleLoading}
                                            className="min-w-0 flex-1 bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400"
                                        />
                                    </div>
                                </Field>
                                <Field id="login-password" label="Password">
                                    <div className="flex h-14 items-center gap-3 px-4">
                                        <FiLock className="h-5 w-5 shrink-0 text-gray-400" />
                                        <input
                                            id="login-password"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="current-password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            disabled={loading || googleLoading}
                                            className="min-w-0 flex-1 bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            className="rounded-lg p-1 text-gray-400 hover:text-gray-700"
                                        >
                                            {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </Field>
                                <button type="submit" disabled={loading || googleLoading} className={primaryBtn}>
                                    {loading ? (<><Spinner /> Signing in...</>) : 'Sign In'}
                                </button>
                            </form>
                        )}

                        <button
                            type="button"
                            onClick={() => switchTab(tab === 'phone' ? 'email' : 'phone')}
                            className="mt-5 block w-full text-center text-sm font-semibold text-gray-600 hover:text-red-600"
                        >
                            {tab === 'phone' ? 'Use email and password instead' : 'Use phone number instead'}
                        </button>

                        {/* Google */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                <div className="w-full border-t border-gray-200" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="bg-white px-4 text-gray-400">or continue with</span>
                            </div>
                        </div>
                        <div>
                            {googleLoading ? (
                                <div className="flex w-full items-center justify-center gap-3 py-3.5 text-gray-600">
                                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" aria-hidden="true" />
                                    Signing in with Google...
                                </div>
                            ) : (
                                <div className="flex justify-center">
                                    <GoogleLogin
                                        onSuccess={handleGoogleSignIn}
                                        onError={handleGoogleError}
                                        text="continue_with"
                                        shape="pill"
                                        size="large"
                                        width="280"
                                    />
                                </div>
                            )}
                        </div>

                        <p className="mt-8 text-center text-sm text-gray-500">
                            Don't have an account?{' '}
                            <Link to="/register" className="font-semibold text-red-600 hover:underline">
                                Create one now
                            </Link>
                        </p>
                        <p className="mt-4 text-center text-xs leading-5 text-gray-400">
                            By continuing, you agree to our{' '}
                            <Link to={TERMS_PATH} className="text-red-600 hover:underline">Terms of Use</Link>
                            {' '}&amp;{' '}
                            <Link to={PRIVACY_PATH} className="text-red-600 hover:underline">Privacy Policy</Link>
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}